import React, { useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { computeLeaders } from "@/lib/helpers";
import { computeBonusAwards } from "@/lib/awards";
import Ceremony, { markCeremonySeen } from "@/pages/Ceremony";
import { PartyPopper, RotateCcw, Trophy, Sprout, Fish } from "lucide-react";

function Podium({ leaders }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-end">
      {[
        { key: "smallest", label: "Smallest", data: leaders.smallest, statSuffix: "cm", ord: 0, height: "h-24" },
        { key: "biggest", label: "Biggest", data: leaders.biggest, statSuffix: "cm", ord: 1, height: "h-32" },
        { key: "most-caught", label: "Most Caught", data: leaders.mostCaught, statSuffix: "", ord: 2, height: "h-28" },
      ]
        .sort((a, b) => a.ord - b.ord)
        .map((p) => (
          <div key={p.key} className="flex flex-col items-center gap-2">
            {p.data?.member ? (
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-3xl"
                style={{ background: p.data.member.color + "33" }}
              >
                {p.data.member.emoji}
              </div>
            ) : (
              <div className="w-14 h-14 rounded-full bg-white/50 flex items-center justify-center text-2xl">
                🥄
              </div>
            )}
            <div className="font-display font-black text-sm text-[#0A3D62] text-center leading-tight truncate w-full">
              {p.data?.member?.name || "—"}
            </div>
            <div className="font-display text-lg font-black text-[#E8A838]">
              {p.data ? (p.key === "most-caught" ? p.data.count : `${p.data.cm.toFixed(0)}${p.statSuffix}`) : "—"}
            </div>
            <div
              className={`${p.height} w-full rounded-t-2xl bg-gradient-to-b from-[#E8A838] to-[#c98e1e] shadow-inner flex items-start justify-center pt-2`}
            >
              <div className="text-[10px] font-bold text-[#1A2A35] uppercase tracking-widest">
                {p.label}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

export default function Results() {
  const { family, members, catches } = useApp();
  const leaders = useMemo(() => computeLeaders(catches, members), [catches, members]);
  const bonus = useMemo(() => computeBonusAwards(catches, members), [catches, members]);
  const [replayCeremony, setReplayCeremony] = useState(false);
  const year = new Date(family.tournament_end).getFullYear();

  if (replayCeremony) {
    return (
      <Ceremony
        onDone={() => {
          markCeremonySeen(family);
          setReplayCeremony(false);
        }}
      />
    );
  }

  return (
    <div className="px-5 pt-4 pb-4 space-y-6" data-testid="results-screen">
      {/* Banner */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0A3D62] to-[#082e4a] text-[#FAF8F3] p-6 border-2 border-[#E8A838]/40 relative overflow-hidden">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E8A838] flex items-center gap-2">
          <PartyPopper className="w-4 h-4" strokeWidth={2.5} />
          Final Standings
        </div>
        <div className="font-display font-black text-3xl leading-tight mt-2">
          {family.comp_name}
        </div>
        <div className="text-sm opacity-75 mt-1">Easter {year} · Comp closed</div>
        <div className="mt-6">
          <Podium leaders={leaders} />
        </div>
      </div>

      {/* Big podium detail cards */}
      <div className="space-y-3">
        <ChampCard
          icon={<Trophy className="w-6 h-6" strokeWidth={2.5} />}
          testId="results-biggest"
          rank="🏆 Champion"
          title="Biggest Fish"
          leader={leaders.biggest}
          statSuffix="cm"
        />
        <ChampCard
          icon={<Fish className="w-6 h-6" strokeWidth={2.5} />}
          testId="results-most-caught"
          rank="🎣 Volume winner"
          title="Most Caught"
          leader={leaders.mostCaught}
          statField="count"
        />
        <ChampCard
          icon={<Sprout className="w-6 h-6" strokeWidth={2.5} />}
          testId="results-smallest"
          rank="🐟 Proud minnow"
          title="Smallest Fish"
          leader={leaders.smallest}
          statSuffix="cm"
        />
      </div>

      {/* Bonus awards */}
      {bonus.length > 0 && (
        <div>
          <h2 className="font-display text-lg font-black text-[#0A3D62] mb-3">Bonus awards</h2>
          <div className="space-y-2">
            {bonus.map((a) => (
              <div
                key={a.key}
                className="bg-white rounded-2xl p-4 border border-[#0A3D62]/5 flex items-center gap-3"
                data-testid={`results-bonus-${a.key}`}
              >
                <div className="text-3xl w-12 text-center">{a.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B7D]">
                    {a.title}
                  </div>
                  <div className="font-display text-lg font-black text-[#0A3D62] flex items-center gap-2">
                    <span className="text-xl">{a.member?.emoji}</span>
                    {a.member?.name}
                  </div>
                  <div className="text-xs text-[#556B7D] italic mt-0.5">"{a.copy}"</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setReplayCeremony(true)}
        data-testid="results-replay-ceremony"
        className="w-full h-14 rounded-2xl bg-white border border-[#0A3D62]/10 text-[#0A3D62] font-bold flex items-center justify-center gap-2 active:scale-[0.98]"
      >
        <RotateCcw className="w-5 h-5" strokeWidth={2.5} />
        Replay the ceremony
      </button>
    </div>
  );
}

function ChampCard({ icon, rank, title, leader, statSuffix, statField, testId }) {
  return (
    <div
      className="bg-white rounded-3xl p-5 border-2 border-[#E8A838]/30 flex items-center gap-4"
      data-testid={testId}
    >
      {leader?.member ? (
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-3xl shrink-0"
          style={{ background: leader.member.color + "33" }}
        >
          {leader.member.emoji}
        </div>
      ) : (
        <div className="w-14 h-14 rounded-full bg-[#FAF8F3] flex items-center justify-center text-2xl shrink-0">
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#E8A838]">{rank}</div>
        <div className="font-display text-lg font-black text-[#0A3D62]">{title}</div>
        <div className="text-sm text-[#556B7D] truncate">{leader?.member?.name || "Nobody"}</div>
      </div>
      <div className="font-display text-3xl font-black text-[#E8A838] shrink-0">
        {leader ? (statField === "count" ? leader.count : `${leader.cm.toFixed(0)}${statSuffix}`) : "—"}
      </div>
    </div>
  );
}
